import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SmartLinkFilter } from '../smlFilterTabs/types';
import {
  SMARTLINKS_SEARCH_PARAM,
  SMARTLINKS_TAB_PARAM,
  smartLinksOverviewQuery,
  smartLinksTabOf,
} from './smartLinksOverviewQuery';

export interface SmartLinksOverviewParams {
  tab: SmartLinkFilter;
  search: string;
  /** A tab click is a new history entry, so Back returns to the tab before. */
  setTab: (tab: SmartLinkFilter) => void;
  /** The search replaces the history entry: Back does not step through each letter. */
  setSearch: (search: string) => void;
}

/** The tab and the search of the SmartLinks page live in the URL (reload, Back, deep links). */
export function useSmartLinksOverviewParams(): SmartLinksOverviewParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = smartLinksTabOf(searchParams.get(SMARTLINKS_TAB_PARAM));
  const search = searchParams.get(SMARTLINKS_SEARCH_PARAM) ?? '';

  const setTab = useCallback(
    (next: SmartLinkFilter) => {
      setSearchParams((current) => smartLinksOverviewQuery(current, { tab: next }));
    },
    [setSearchParams]
  );

  const setSearch = useCallback(
    (next: string) => {
      setSearchParams((current) => smartLinksOverviewQuery(current, { search: next }), { replace: true });
    },
    [setSearchParams]
  );

  return useMemo(() => ({ tab, search, setTab, setSearch }), [tab, search, setTab, setSearch]);
}
